import { Colors, Icon, Text, TextInput } from '@amelio/ui-library';
import { useState } from 'react';

import {
    AGENDA_DEFAULT_TOPIC_DURATION_MINUTES,
    AGENDA_ITEM_MAX_DURATION_MINUTES,
    AGENDA_ITEM_TITLE_MAX_LENGTH,
} from '../agendaLimits';

import { useAmelioTranslations } from '@/hooks';

interface TopicEditFormProps {
    initialTitle?: string;
    initialDurationMinutes?: number;
    onConfirm: (title: string, durationMinutes: number) => void;
    onCancel: () => void;
    dataTestId?: string;
}

export const TopicEditForm = ({
    initialTitle = '',
    initialDurationMinutes = AGENDA_DEFAULT_TOPIC_DURATION_MINUTES,
    onConfirm,
    onCancel,
    dataTestId = 'topic-edit-form',
}: TopicEditFormProps) => {
    const { t } = useAmelioTranslations();
    const [title, setTitle] = useState(initialTitle);
    const [durationMinutes, setDurationMinutes] = useState(String(initialDurationMinutes));

    const handleConfirm = () => {
        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            return;
        }

        onConfirm(
            trimmedTitle,
            Math.min(
                AGENDA_ITEM_MAX_DURATION_MINUTES,
                Math.max(1, parseInt(durationMinutes, 10) || 1),
            ),
        );
    };

    return (
        <div
            className={'flex w-full items-center gap-3'}
            data-testid={dataTestId}
        >
            <div className={'flex-1'}>
                <TextInput
                    value={title}
                    onChange={setTitle}
                    placeholder={t('meetingAgenda.newTopicPlaceholder')}
                    maxLength={AGENDA_ITEM_TITLE_MAX_LENGTH}
                    dataTestId={`${dataTestId}-title-input`}
                    className={
                        'border-primary-300/30 focus:!ring-primary flex-1 border bg-transparent px-4 py-2 focus:!ring-2'
                    }
                />
            </div>

            <div className={'flex items-center gap-1'}>
                <input
                    type={'number'}
                    min={1}
                    max={AGENDA_ITEM_MAX_DURATION_MINUTES}
                    step={1}
                    value={durationMinutes}
                    onChange={(event) => {
                        const raw = event.target.value;
                        if (raw === '') {
                            setDurationMinutes('');
                            return;
                        }
                        const parsed = parseInt(raw, 10);
                        if (!Number.isNaN(parsed) && parsed >= 1) {
                            setDurationMinutes(
                                String(Math.min(parsed, AGENDA_ITEM_MAX_DURATION_MINUTES)),
                            );
                        }
                    }}
                    onKeyDown={(event) => {
                        if (['-', '+', '.', 'e', 'E'].includes(event.key)) {
                            event.preventDefault();
                        }
                    }}
                    onBlur={() => {
                        const parsed = parseInt(durationMinutes, 10);
                        if (Number.isNaN(parsed) || parsed < 1) {
                            setDurationMinutes('1');
                        }
                    }}
                    className={
                        'focus:border-primary-500 border-base-200 text-base-500 w-16 rounded-lg border px-4 py-2 text-sm focus:outline-none'
                    }
                    data-testid={`${dataTestId}-duration-input`}
                />
                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                >
                    {t('meetingAgenda.minutesShort')}
                </Text>
            </div>

            <button
                type={'button'}
                onClick={handleConfirm}
                // `px-3 py-3` keeps the same 40 x 40 target as before: the icon shrinks to the
                // row's trash-icon size (`sm`), the padding grows to make up the difference.
                className={
                    'bg-primary-500 hover:bg-primary-600 flex shrink-0 items-center justify-center rounded-lg px-3 py-3'
                }
                aria-label={t('meetingAgenda.confirmTopic')}
                data-testid={`${dataTestId}-confirm-button`}
            >
                <Icon
                    name={'essential-checkmark'}
                    size={'sm'}
                    color={Colors.WHITE}
                />
            </button>

            <button
                type={'button'}
                onClick={onCancel}
                className={
                    'border-base-200 bg-base-100 hover:bg-base-50 flex shrink-0 items-center justify-center rounded-lg border px-3 py-3'
                }
                aria-label={t('meetingAgenda.cancelTopicEdit')}
                data-testid={`${dataTestId}-cancel-button`}
            >
                <Icon
                    name={'essential-close-circle'}
                    size={'sm'}
                    color={Colors.BASE_400}
                />
            </button>
        </div>
    );
};

export default TopicEditForm;
